import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MonkeyPatchGenerator from './MonkeyPatchGenerator';
import RouteScriptGenerator from './RouteScriptGenerator';
import {
    selectOffsetSettings,
    selectIsAutoOffset,
    setSpecificCenterOffset,
    setIsAutoOffset
} from '../mapSlice';
import styles from './DebugControls.module.css';

const DebugControls = (props) => {
    const { debugEnabled, setDebugEnabled } = props;
    const dispatch = useDispatch();
    const offsetSettings = useSelector(selectOffsetSettings);
    const isAutoOffset = useSelector(selectIsAutoOffset);

    if (!debugEnabled) {
        return null;
    }

    const {
        routeProximity,
        onProximityChange,
        isDrivingMode,
        isRoutingMode,
        currentBearing,
        currentSpeed,
        activeLightDistance,
        intersectionState,
        onBearingChange,
        centerOffset,
        onCenterOffsetChange
    } = props;

    // Determine which offset mode is active for display
    // We don't have isHudVisible passed explicitly as boolean, but we can infer or pass it if needed.
    // Actually, centerOffset passed here IS the active one.
    // The user knows if HUD is visible.
    // Let's just label it "Active Center Offset".

    return (
        <div className={`leaflet-control leaflet-bar ${styles.Container}`}>
            <div className={styles.Header}>
                <span>Debug Info</span>
                <button
                    className={styles.CloseButton}
                    onClick={() => setDebugEnabled(false)}
                    aria-label="Close Debug Controls"
                >
                    ✕
                </button>
            </div>

            <div className={styles.Content}>
                <div className={styles.Row}>Mode: {isDrivingMode ? 'Driving' : isRoutingMode ? 'Routing' : 'Browsing'}</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }} className={styles.Row}>
                    <span>Bearing: {Math.round(currentBearing)}°</span>
                </div>
                {debugEnabled && (
                    <input
                        type="range"
                        min="0"
                        max="360"
                        value={Math.round(currentBearing || 0)}
                        onChange={onBearingChange}
                        className={styles.RangeInput}
                    />
                )}
                <div className={styles.Row}>Speed: {currentSpeed} km/h</div>
                {debugEnabled && (
                    <input
                        type="range"
                        min="0"
                        max="120"
                        value={Math.round(currentSpeed || 0)}
                        onChange={props.onSpeedChange}
                        className={styles.RangeInput}
                    />
                )}
                <div className={styles.Row}>Next Light: {activeLightDistance ? `${Math.round(activeLightDistance)}m` : 'N/A'}</div>
                <div className={styles.Row}>Time to Light: {activeLightDistance && currentSpeed > 0 ? `${Math.round(activeLightDistance / (currentSpeed / 3.6))}s` : 'N/A'}</div>
                {intersectionState && (
                    <>
                        <div className={styles.Divider}></div>
                        <div className={styles.Row}>Entry Heading: {intersectionState.entryHeading != null ? `${Math.round(intersectionState.entryHeading)}°` : 'N/A'}</div>
                        <div className={styles.Row}>Exit Heading: {intersectionState.exitHeading != null ? `${Math.round(intersectionState.exitHeading)}°` : 'N/A'}</div>
                    </>
                )}

                <div className={styles.Divider}></div>

                <div className={styles.Label}>
                    Traffic Light Proximity: {routeProximity}m
                </div>
                <input
                    type="range"
                    min="10"
                    max="100"
                    value={routeProximity}
                    onChange={onProximityChange}
                    className={styles.RangeInput}
                />

                <div className={styles.Label}>
                    HUD Distance: {props.hudDistanceThreshold}m
                </div>
                <input
                    type="range"
                    min="50"
                    max="300"
                    step="25"
                    value={props.hudDistanceThreshold}
                    onChange={props.onHudDistanceChange}
                    className={styles.RangeInput}
                />

                <div className={styles.Label}>
                    HUD Time: {props.hudTimeThreshold}s
                </div>
                <input
                    type="range"
                    min="5"
                    max="30"
                    step="5"
                    value={props.hudTimeThreshold}
                    onChange={props.onHudTimeChange}
                    className={styles.RangeInput}
                />

                <div className={styles.Divider}></div>
                <div className={styles.Label}>
                    <strong>Map Center Offsets</strong>
                </div>

                <div className={styles.Row} style={{ justifyContent: 'flex-start', gap: '10px' }}>
                    <input
                        type="checkbox"
                        checked={isAutoOffset}
                        onChange={(e) => dispatch(setIsAutoOffset(e.target.checked))}
                    />
                    <span>Auto-Calculate Offsets</span>
                </div>

                {/* Default Offset Controls */}
                <div className={styles.Label} style={{ marginTop: '10px', color: '#aaa' }}>
                    Default (No HUD)
                </div>
                <div className={styles.Row}>
                    <span>X: {offsetSettings.default.x}</span>
                    <span>Y: {offsetSettings.default.y}</span>
                </div>
                <div className={styles.Label}>Off X</div>
                <input
                    type="range"
                    min="-200"
                    max="200"
                    step="10"
                    disabled={isAutoOffset}
                    value={offsetSettings.default.x}
                    onChange={(e) => dispatch(setSpecificCenterOffset({ mode: 'default', offset: { ...offsetSettings.default, x: parseInt(e.target.value, 10) } }))}
                    className={styles.RangeInput}
                />
                <div className={styles.Label}>Off Y</div>
                <input
                    type="range"
                    min="-300"
                    max="300"
                    step="10"
                    disabled={isAutoOffset}
                    value={offsetSettings.default.y}
                    onChange={(e) => dispatch(setSpecificCenterOffset({ mode: 'default', offset: { ...offsetSettings.default, y: parseInt(e.target.value, 10) } }))}
                    className={styles.RangeInput}
                />

                {/* HUD Offset Controls */}
                <div className={styles.Label} style={{ marginTop: '10px', color: '#aaa' }}>
                    HUD Visible
                </div>
                <div className={styles.Row}>
                    <span>X: {offsetSettings.hud.x}</span>
                    <span>Y: {offsetSettings.hud.y}</span>
                </div>
                <div className={styles.Label}>Off X</div>
                <input
                    type="range"
                    min="-200"
                    max="200"
                    step="10"
                    disabled={isAutoOffset}
                    value={offsetSettings.hud.x}
                    onChange={(e) => dispatch(setSpecificCenterOffset({ mode: 'hud', offset: { ...offsetSettings.hud, x: parseInt(e.target.value, 10) } }))}
                    className={styles.RangeInput}
                />
                <div className={styles.Label}>Off Y</div>
                <input
                    type="range"
                    min="-300"
                    max="300"
                    step="10"
                    disabled={isAutoOffset}
                    value={offsetSettings.hud.y}
                    onChange={(e) => dispatch(setSpecificCenterOffset({ mode: 'hud', offset: { ...offsetSettings.hud, y: parseInt(e.target.value, 10) } }))}
                    className={styles.RangeInput}
                />

                {/* New console-based simulation generators */}
                <MonkeyPatchGenerator />
                <RouteScriptGenerator />
            </div>
        </div>
    );
};

export default DebugControls;
